import { useAuth } from "@dl/web-core-js/auth";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import { Badge, Button, Dialog } from "@dl/web-core-js/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  InventoryRuleRunClient,
  ProductInventoryRuleClient,
  SalesChannelClient,
  type InventoryRuleAutomationReason,
  type InventoryRuleChangeDTO,
  type InventoryRuleCounts,
  type InventoryRuleRunDTO,
} from "../api/inventory";
import type { InventoryRuleMode } from "../api/product";
import { modeLabel } from "./product/InventoryRulesTab";

/**
 * The inventory rule job, run by hand: what it would change now (a dry run,
 * nothing written), then Apply. The same job runs every night at the end of
 * the KPI recompute while the switch on the Settings page is on; Apply works
 * whatever the switch says.
 */
export function InventoryRuleJobDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const queryClient = useQueryClient();
  const ruleClient = useMemo(() => new ProductInventoryRuleClient(http), [http]);
  const channelClient = useMemo(() => new SalesChannelClient(http), [http]);

  const preview = useQuery({
    queryKey: ["inventory-rule", "preview"],
    queryFn: () => ruleClient.previewAutomation(),
    enabled: open,
    staleTime: 0,
  });
  const { data: channels } = useQuery({
    queryKey: ["sales-channel", "all"],
    queryFn: () => channelClient.findAll(),
    enabled: open,
    staleTime: 30 * 60_000,
  });
  const channelName = useMemo(() => new Map((channels ?? []).map((channel) => [channel.id, channel.name])), [channels]);

  const apply = useMutation({
    mutationFn: () => ruleClient.runAutomation(),
    onSuccess: async (run) => {
      toast.success(t("inv-rules.run.done", "Inventory rules applied: {{summary}}", { summary: countsSummary(t, run) }));
      await queryClient.invalidateQueries({ queryKey: ["inventory-rule"] });
      await queryClient.invalidateQueries({ queryKey: ["product"] });
      onClose();
    },
    onError: (failure) => toast.error(failure instanceof Error ? failure.message : t("error.generic", "Failed")),
  });

  const cols = useMemo(() => {
    const c = defineColumns<InventoryRuleChangeDTO>();
    return [
      c.text("sku", (row) => row.sku ?? `#${row.product_id}`, { headerKey: "inv-rules.sku", priority: 1 }),
      c.text("channel", (row) => channelName.get(row.sales_channel_id) ?? `#${row.sales_channel_id}`, {
        headerKey: "product.rules.platform",
        filter: "select",
        priority: 1,
      }),
      c.text("before", (row) => ruleText(t, row.old_mode, row.old_floor_quantity, row.old_is_listed), {
        headerKey: "inv-rules.before",
        filter: "select",
        priority: 2,
      }),
      c.text("after", (row) => ruleText(t, row.new_mode, row.new_floor_quantity, row.new_is_listed), {
        headerKey: "inv-rules.after",
        filter: "select",
        priority: 1,
      }),
      c.custom(
        "reason",
        (row) => (row.is_held_by_lock ? "LOCKED" : row.reason),
        (row) =>
          row.is_held_by_lock ? (
            <Badge size="sm" variant="warning">
              {t("inv-rules.held", "Kept: locked")}
            </Badge>
          ) : (
            <span>{automationReasonLabel(t, row.reason)}</span>
          ),
        { headerKey: "inv-rules.reason", filter: "select", priority: 2 },
      ),
    ];
  }, [channelName, t]);

  const data = preview.data;
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t("inv-rules.title", "Apply inventory rules")}
      description={
        data
          ? t(
              "inv-rules.description",
              "Top {{ca}} single items in Canada and top {{us}} in the US floor at each channel's default floor; everything else follows status. Locked rules are kept.",
              { ca: data.floor_top_ca, us: data.floor_top_us },
            )
          : undefined
      }
      size="xl"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={apply.isPending}>
            {t("button.cancel", "Cancel")}
          </Button>
          <Button
            variant="primary"
            onClick={() => apply.mutate()}
            loading={apply.isPending}
            disabled={!data || preview.isFetching}
          >
            {t("inv-rules.apply", "Apply now")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {data && <p className="text-[13px] text-ink">{countsSummary(t, data)}</p>}
        <DataTable
          data={data?.changes ?? []}
          columns={cols}
          getRowId={(row) => `${row.product_inventory_rule_id}`}
          loading={preview.isLoading}
          emptyMessage={t("inv-rules.none", "Every rule already matches.")}
        />
      </div>
    </Dialog>
  );
}

/** The last run, one line: for the Settings page. */
export function useLastInventoryRuleRun() {
  const { http } = useAuth();
  const client = useMemo(() => new InventoryRuleRunClient(http), [http]);
  return useQuery({
    queryKey: ["inventory-rule", "runs"],
    queryFn: async (): Promise<InventoryRuleRunDTO | null> => (await client.findAll())[0] ?? null,
  });
}

export function countsSummary(t: (key: string, fallback: string, options?: Record<string, unknown>) => string, counts: InventoryRuleCounts): string {
  // Only what moved: "100 floored · 660 dispatchable · 792 kept by their lock".
  const parts = [
    counts.to_floor_count > 0 && t("inv-rules.count.floor", "{{count}} floored", { count: counts.to_floor_count }),
    counts.to_dispatchable_count > 0 && t("inv-rules.count.dispatchable", "{{count}} dispatchable", { count: counts.to_dispatchable_count }),
    counts.to_blocked_count > 0 && t("inv-rules.count.blocked", "{{count}} blocked", { count: counts.to_blocked_count }),
    counts.unlisted_count > 0 && t("inv-rules.count.unlisted", "{{count}} unlisted", { count: counts.unlisted_count }),
    counts.locked_skipped_count > 0 && t("inv-rules.count.held", "{{count}} kept by their lock", { count: counts.locked_skipped_count }),
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(" · ") : t("inv-rules.count.none", "No change");
}

function ruleText(
  t: (key: string, fallback: string) => string,
  mode: InventoryRuleMode,
  floor: number | null | undefined,
  listed: boolean,
): string {
  const text = mode === "FLOOR" ? `${modeLabel(t, mode)} ${floor ?? ""}`.trim() : modeLabel(t, mode);
  return listed ? text : `${text} · ${t("product.rules.listed.no", "Not listed")}`;
}

function automationReasonLabel(t: (key: string, fallback: string) => string, reason: InventoryRuleAutomationReason): string {
  switch (reason) {
    case "COMPONENT":
      return t("inv-rules.reason.component", "Component");
    case "DISCONTINUED":
      return t("inv-rules.reason.discontinued", "Discontinued");
    case "NOT_LAUNCHED":
      return t("inv-rules.reason.not-launched", "Not launched");
    case "PENDING_DISCONTINUED":
      return t("inv-rules.reason.pending-discontinued", "Pending discontinuation");
    case "TOP_SELLER":
      return t("inv-rules.reason.top-seller", "Top seller");
    case "NEW_PRODUCT":
      return t("inv-rules.reason.new-product", "New product");
    case "IN_PRODUCTION":
      return t("inv-rules.reason.in-production", "In production");
    case "DEFAULT":
      return t("inv-rules.reason.default", "Default");
    case "LOCKED":
      return t("inv-rules.reason.locked", "Locked");
  }
}
